import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
} from 'semantic-ui-react';

import { errorType, keyType } from '../types';
import ConsoleErrorMessage from '../utils/console-error-message';

import { clearError, postKey } from '../../modules/keys';
import { copyStringToClipboard } from '../../modules/utils';

/**
 * This timeout is to give the modal time to mount to the DOM.
 * Otherwise it will be called .focus() on an undefined object.
 */
const SET_REF_TIMEOUT_MS = 10;

export class CreateKeyModal extends Component {
  state = {
    createError: false,
    newKeyName: '',
    newSecret: '',
    password: '',
    ref: null,
  }

  componentDidMount() {
    const { connectedClearError } = this.props;
    connectedClearError();
  }

  componentDidUpdate(prevProps) {
    const { error, postKeyRequestOut } = this.props;
    if (prevProps.postKeyRequestOut === true && postKeyRequestOut === false
      && error) {
      this.setState({ createError: true }); /* eslint-disable-line react/no-did-update-set-state */
    }
  }

  setRef = (ref) => {
    this.setState({ ref });
  }

  focusOnInput = () => {
    setTimeout(() => {
      const { ref } = this.state;
      if (ref) {
        ref.focus();
      }
    }, SET_REF_TIMEOUT_MS);
  }

  handleChange = (field, value) => {
    const stateChange = {};
    stateChange[field] = value;
    this.setState(stateChange);
  }

  handleCreateModalClose = () => {
    const { connectedClearError } = this.props;
    this.setState({
      createError: false,
      newKeyName: '',
      newSecret: '',
      password: '',
    });
    connectedClearError();
  }

  /*
   * NOTE: This is quite hacky; basically implicit form submission
   * -- when you hit enter to submit the form -- would work if there
   * is only one field in the modal, but with two fields it doesn't.
   * It doesn't matter which of the two I remove.
   *
   * Therefore we set the onKeyPress prop of the form...
   */
  handleKeyPress = (e) => {
    const { postKeyRequestOut } = this.props;
    const { password } = this.state;
    if (!postKeyRequestOut && !!password && e.key === 'Enter') {
      this.handleSubmit(e);
    }
  }

  handleSubmit = (event) => {
    const { connectedClearError, connectedPostKey } = this.props;
    const { newKeyName, password } = this.state;
    event.preventDefault();
    connectedClearError();
    this.setState({ createError: false });

    connectedPostKey({ name: newKeyName, password })
      .then(() => {
        const { error, keys } = this.props;
        if (error) { return; }

        const newSecret = keys[0].api_key || keys[0].secret;
        this.setState({
          newSecret,
        });
      });
  }

  updateNewKeyName = (event) => {
    event.preventDefault();
    this.handleChange('newKeyName', event.target.value);
  }

  updatePasswordField = (event) => {
    const { connectedClearError, error } = this.props;
    event.preventDefault();
    if (error) {
      connectedClearError();
      this.setState({ createError: false });
    }
    this.handleChange('password', event.target.value);
  }

  renderCreatedKeyDiv = () => {
    const { newKeyName, newSecret } = this.state;
    return (
      <div>
        <p>
          Your new key has been created. Below is the &apos;secret&apos; for the key.
          This secret will only be shown once and is not recoverable later.
        </p>
        <p>
          <em>Name:</em>
          {' '}
          { newKeyName }
        </p>
        <p>
          <em>Secret:</em>
          {' '}
          { newSecret }
        </p>
      </div>
    );
  }

  renderCreateKeyAction = () => {
    const { postKeyRequestOut } = this.props;
    const { newSecret, password } = this.state;
    return newSecret
      ? (
        <Button
          content="Copy Secret"
          onClick={() => copyStringToClipboard(newSecret)}
          primary
          type="button"
        />
      ) : (
        <Button
          content="Create"
          disabled={!password || postKeyRequestOut}
          loading={postKeyRequestOut}
          onClick={this.handleSubmit}
          primary
          type="submit"
        />
      );
  }

  /*
   * The reason why the key name input field is outside Form
   * is so Chrome (or any other browser) doesn't interpret it
   * as a username field and autofill it.
   */
  renderCreateKeyForm = () => {
    const { email } = this.props;
    const { newKeyName, password } = this.state;
    return (
      <div className="re is-marginTop-8px">
        <Form onKeyPress={this.handleKeyPress} onSubmit={this.handleSubmit}>
          <div className="re is-marginBottom-1em">
            {`Currently signed in as: ${email}`}
            <Input
              className="re is-display-none key-action-email"
              readOnly
              transparent
              type="text"
              value={email}
            />
          </div>
          <Form.Field>
            <Input
              onChange={this.updatePasswordField}
              placeholder="Password"
              ref={this.setRef}
              required
              type="password"
              value={password}
            />
          </Form.Field>
        </Form>
        <Input
          className="re is-marginTop-1em"
          fluid
          onChange={this.updateNewKeyName}
          onKeyPress={this.handleKeyPress}
          placeholder="Key name (optional)"
          type="text"
          value={newKeyName}
        />
      </div>
    );
  }

  renderCreateError = () => {
    const { error } = this.props;
    return (
      <Message error>
        <ConsoleErrorMessage error={error} showSignIn={false} />
      </Message>
    );
  }

  render() {
    const { postKeyRequestOut } = this.props;
    const { createError, newSecret } = this.state;
    return (
      <Modal
        closeOnDimmerClick={!postKeyRequestOut}
        onClose={this.handleCreateModalClose}
        onOpen={this.focusOnInput}
        size="tiny"
        trigger={(
          <Button
            className="re is-marginBottom-4px"
            content="Create a Key"
            primary
            type="button"
          />
        )}
      >
        <Modal.Header>
          Create a new API Key
        </Modal.Header>
        <Modal.Content>
          <p>Enter your password to create a new key.</p>
          { !newSecret && this.renderCreateKeyForm() }
          { !createError && newSecret && this.renderCreatedKeyDiv() }
          { createError && this.renderCreateError() }
        </Modal.Content>
        <Modal.Actions>
          { this.renderCreateKeyAction() }
        </Modal.Actions>
      </Modal>
    );
  }
}

CreateKeyModal.propTypes = {
  connectedClearError: PropTypes.func.isRequired,
  connectedPostKey: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  error: errorType,
  keys: PropTypes.arrayOf(keyType).isRequired,
  postKeyRequestOut: PropTypes.bool.isRequired,
};

CreateKeyModal.defaultProps = {
  error: undefined,
};

const mapStateToProps = state => ({
  email: state.session.email,
  error: state.keys.error,
  keys: state.keys.keys,
  postKeyRequestOut: state.keys.postKeyRequestOut,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedClearError: clearError,
  connectedPostKey: postKey,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateKeyModal);
